import Navigation from '@/components/Navigation/index';
import { isCancelled } from '@/helpers';
import { _isEmpty, _isNil, _notNil, _isNotEmpty } from '@/littledash';
import type { State, StudyState } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import {
  RiCalendarTodoFill,
  RiEqualizerLine,
  RiFile3Line,
  RiFileDownloadLine,
  RiFileUploadLine,
  RiLayoutMasonryLine,
  RiLineChartLine,
  RiRulerLine,
  RiServerLine,
  RiSipLine,
  RiStethoscopeLine,
  RiSyringeLine,
  RiTable2,
  RiTableLine,
  RiTestTubeLine,
  RiHistoryLine,
} from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { BenchlingSyncIcon } from '@/components/UI/Icons/BenchlingSyncIcon';

// Cache for benchling folder configuration per study to avoid flicker across tab navigation
const benchlingFolderCache: Map<string, boolean> = new Map();

interface StudyNavProps {
  route: {
    path: string;
    private: boolean;
    exact: boolean;
    canCollapseNav: boolean;
  };
}

const getState = (reviewState: StudyState) => {
  switch (reviewState) {
    case 'in_progress':
      return {
        animals: false,
        cages: false,
        graphs: false,
        workflow: false,
        treatmentGroups: false,
        attachments: false,
        samples: false,
        observations: false,
        doses: false,
        import: false,
        export: false,
        benchlingSync: false,
        settings: false,
        dataTables: false,
      };
    case 'awaiting_approval':
      return {
        overview: false,
        animals: true,
        cages: true,
        graphs: true,
        workflow: true,
        treatmentGroups: false,
        attachments: false,
        observations: true,
        doses: true,
        import: true,
        export: true,
        benchlingSync: true,
        settings: false,
      };
    case 'rejected':
      return {
        overview: false,
        animals: true,
        cages: true,
        graphs: true,
        workflow: true,
        treatmentGroups: true,
        attachments: false,
        samples: true,
        observations: true,
        doses: true,
        import: true,
        export: true,
        benchlingSync: true,
        settings: false,
      };
    case 'cancelled':
    default:
      return {
        overview: false,
        animals: true,
        cages: true,
        graphs: true,
        workflow: true,
        treatmentGroups: true,
        attachments: true,
        samples: true,
        observations: true,
        doses: true,
        import: true,
        export: true,
        benchlingSync: true,
        settings: false,
      };
  }
};

// Helper function to check if a study has a benchling folder configured
const hasBenchlingFolderConfigured = (study: Study | null | undefined): boolean => {
  if (!study || !study.metadata) {
    return false;
  }

  const benchlingFolderMeta = study.metadata.find((meta) => meta.slug === BENCHLING_INTEGRATION_META_SLUG);

  return !!(benchlingFolderMeta && benchlingFolderMeta.value && _isNotEmpty(benchlingFolderMeta.value));
};

const StudyNav: FC<StudyNavProps> = ({ route }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const auditLogViewEnabled = useSelector(({ team: { features } }: State) => features?.audit_log_view === true);
  const { id } = useParams();
  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id },
    includes: 'metadata',
  });

  const { studyState, currentStudyDetails } = useSelector(
    ({
      ui: {
        settings: { studyState },
        currentStudyDetails,
      },
    }: State) => ({
      studyState,
      currentStudyDetails,
    })
  );

  // Robust benchling folder check using a cache to avoid flicker across tab navigation
  const hasBenchlingFolder = useMemo(() => {
    if (id) {
      // If we already have a cached value for this study and the study is still loading, return it
      const cached = benchlingFolderCache.get(id);
      if (cached !== undefined && studyLoading) {
        return cached;
      }

      // If we have study data, compute and cache the result
      if (study && study.metadata) {
        const result = hasBenchlingFolderConfigured(study);
        benchlingFolderCache.set(id, result);
        return result;
      }
    }

    // Fallback: rely on route to avoid flicker when navigating to benchling sync for the first time
    return location.pathname.includes('benchling-sync');
  }, [id, study, studyLoading, location.pathname]);

  useEffect(() => {
    if (!studyLoading && _notNil(study)) {
      if (_notNil(studyState?.reviewState)) {
        const currentStudyState = isCancelled(study) ? 'cancelled' : study.status;
        if (currentStudyState !== studyState.reviewState) {
          dispatch({
            type: 'SET_CURRENT_STUDY_STATUS',
            studyState: {
              reviewState: currentStudyState,
              pageStates: getState(currentStudyState as StudyState),
            },
          });
        }
      }
      if (_notNil(id) && (_isNil(currentStudyDetails?.name) || currentStudyDetails.name !== study.name)) {
        dispatch({
          type: 'SET_STUDY_DETAILS',
          currentStudyDetails: {
            id: id,
            name: study.name,
            code: study.code,
          },
        });
      }
    }
  }, [studyLoading, studyState?.reviewState, study, dispatch]);

  if (_isEmpty(studyState)) {
    return null;
  }

  const navigation = [
    {
      name: 'Overview',
      webRoute: 'studies.overview',
      icon: <RiLayoutMasonryLine />,
    },
    {
      name: 'Schedule',
      webRoute: 'studies.schedule',
      icon: <RiCalendarTodoFill />,
    },
    {
      name: 'Animals',
      webRoute: 'studies.animals',
      icon: <RiTableLine />,
    },
    {
      name: 'Cages',
      webRoute: 'studies.cages',
      icon: <RiServerLine />,
      isDisabled: studyState?.pageStates?.cages ?? true,
    },
    {
      name: 'Graphs',
      webRoute: 'studies.graphs',
      icon: <RiLineChartLine />,
      isDisabled: studyState?.pageStates?.graphs ?? true,
    },
    {
      name: 'Workflow',
      webRoute: 'studies.workflow',
      icon: <RiRulerLine />,
      isDisabled: studyState?.pageStates?.workflow ?? true,
    },
    {
      name: 'Treatment groups',
      webRoute: 'studies.studyGroups',
      icon: <RiSyringeLine />,
      isDisabled: studyState?.pageStates?.treatmentGroups ?? true,
    },
    {
      name: 'Attachments',
      webRoute: 'studies.attachments',
      icon: <RiFile3Line />,
      isDisabled: studyState?.pageStates?.attachments ?? true,
    },
    {
      name: 'Samples',
      webRoute: 'studies.samples',
      icon: <RiTestTubeLine />,
    },
    {
      name: 'Observations',
      webRoute: 'studies.observations',
      icon: <RiStethoscopeLine />,
      isDisabled: studyState?.pageStates?.observations ?? true,
    },
    {
      name: 'Doses',
      webRoute: 'studies.dosing.list',
      icon: <RiSipLine />,
      isDisabled: studyState?.pageStates?.doses ?? true,
    },
    {
      name: 'Data tables',
      webRoute: 'studies.dataTables.list',
      icon: <RiTable2 />,
      isDisabled: studyState?.pageStates?.dataTables ?? true,
    },
    {
      name: 'Import',
      webRoute: 'studies.import',
      icon: <RiFileDownloadLine />,
      isDisabled: studyState?.pageStates?.import ?? true,
    },
    {
      name: 'Export',
      webRoute: 'studies.export',
      icon: <RiFileUploadLine />,
      isDisabled: studyState?.pageStates?.export ?? true,
    },
    ...(hasBenchlingFolder
      ? [
          {
            name: 'Benchling sync',
            webRoute: 'studies.benchlingSync',
            icon: <BenchlingSyncIcon />,
            isDisabled: studyState?.pageStates?.benchlingSync ?? true,
          },
        ]
      : []),
    ...(auditLogViewEnabled
      ? [
          {
            name: 'Audit log',
            webRoute: 'studies.auditLog',
            icon: <RiHistoryLine />,
          },
        ]
      : []),
    {
      name: 'Settings',
      webRoute: 'studies.settings',
      icon: <RiEqualizerLine />,
    },
  ];

  return <Navigation route={{ ...route, navigation }} studyNames={currentStudyDetails} />;
};

export default StudyNav;
